// SPDX-License-Identifier: AGPL-3.0-or-later
import {useEffect, useState} from 'react';

type Callback = (el: Element) => void;
const elementCallbacks = new WeakMap<Element, Callback>();
let sharedObserver: ResizeObserver | null = null;
function getSharedObserver(): ResizeObserver {
	if (sharedObserver) return sharedObserver;
	sharedObserver = new ResizeObserver((entries) => {
		for (const entry of entries) {
			const cb = elementCallbacks.get(entry.target);
			if (cb) cb(entry.target);
		}
	});
	return sharedObserver;
}
function observeElement(el: Element, cb: Callback): () => void {
	elementCallbacks.set(el, cb);
	const obs = getSharedObserver();
	obs.observe(el);
	return () => {
		elementCallbacks.delete(el);
		obs.unobserve(el);
	};
}
export function useTextOverflow(ref: React.RefObject<HTMLElement | null>): boolean {
	const [isOverflowing, setIsOverflowing] = useState(false);
	useEffect(() => {
		const el = ref.current;
		if (!el) {
			setIsOverflowing(false);
			return;
		}
		const checkOverflow = () => {
			const {scrollWidth, clientWidth} = el;
			const overflowing = scrollWidth - clientWidth > 1;
			setIsOverflowing((prev) => (prev === overflowing ? prev : overflowing));
		};
		checkOverflow();
		return observeElement(el, checkOverflow);
	}, [ref]);
	return isOverflowing;
}
